import React from 'react';

import { Alert, Anchor, Box, Loader, Select, Stack, Text, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconInfoCircle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { DroxyAccordion } from 'components/Droxy/DroxyAccordion';
import { DroxyAccordionSection } from 'components/Droxy/DroxyAccordionSection';
import { InputLabelWithInfo } from 'components/InputLabelWithInfo';
import { SectionNumber } from 'components/SectionNumber';
import { ToolConfigurationPanelWrapper } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolConfigurationPanelWrapper';
import { ToolStatusSection } from 'pages/ManageSmartAssistant/Sections/ToolsSection/ToolConfigurationPanels/ToolStatusSection';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { CaldotcomService } from 'services/caldotcom-service';
import { APPOINTMENT_SCHEDULING_TOOL_GUIDE_URL } from 'shared/constants/phone-smart-assistant';
import { CALDOTCOM_ACCOUNTS_QUERY_KEY, CALDOTCOM_EVENT_TYPES_QUERY_KEY } from 'shared/constants/query-keys';
import { CaldotcomCheckAvailabilityToolConfig } from 'shared/interfaces/tools/caldotcom/CaldotcomCheckAvailability';
import { CaldotcomScheduleMeetingToolConfig } from 'shared/interfaces/tools/caldotcom/CaldotcomScheduleMeeting';
import { ToolConfig, ToolKind, ToolTrigger } from 'shared/interfaces/tools/Tool';
import { getToolConfig } from 'shared/interfaces/tools/utils';
import { formHasFieldErrors } from 'shared/utils/form';
import { requiredInputValidator } from 'shared/utils/form-validators';

enum CaldotcomAppointmentPanelSections {
  GENERAL_SETTINGS = 'general-settings'
}

interface CaldotcomAppointmentToolConfigurationProps {
  onUpdate: (toolConfigs: ToolConfig[]) => void;
  onBack: () => void;
}

export function CaldotcomAppointmenToolConfigurationPanel({
  onUpdate,
  onBack
}: CaldotcomAppointmentToolConfigurationProps) {
  const location = useLocation();
  const toolConfigs: ToolConfig[] = location.state?.toolConfigs ?? [];
  const maybeExistingScheduleMeetingToolConfig = getToolConfig(toolConfigs, ToolKind.CALDOTCOM_SCHEDULE_MEETING);
  const maybeExistingCheckAvailabilityToolConfig = getToolConfig(toolConfigs, ToolKind.CALDOTCOM_CHECK_AVAILABILITY);
  const isNewToolConfig = !maybeExistingScheduleMeetingToolConfig && !maybeExistingCheckAvailabilityToolConfig;

  const configurationForm = useForm({
    initialValues: {
      enabled: maybeExistingScheduleMeetingToolConfig?.enabled ?? true,
      caldotcomAccountId: maybeExistingScheduleMeetingToolConfig?.accountId || '',
      eventTypeId: maybeExistingScheduleMeetingToolConfig?.eventTypeId.toString() || ''
    },
    validate: {
      caldotcomAccountId: (value: string) => requiredInputValidator(value),
      eventTypeId: (value: string) => requiredInputValidator(value)
    }
  });

  const { t } = useTranslation();

  const caldotcomAccountsQuery = useQuery({
    queryKey: [CALDOTCOM_ACCOUNTS_QUERY_KEY],
    queryFn: () => CaldotcomService.getConnectedAccounts()
  });

  const caldotcomEventTypesQuery = useQuery({
    queryKey: [CALDOTCOM_EVENT_TYPES_QUERY_KEY, configurationForm.values.caldotcomAccountId],
    queryFn: () => CaldotcomService.getEventTypes(configurationForm.values.caldotcomAccountId),
    enabled: !!configurationForm.values.caldotcomAccountId
  });

  const handleUpdate = () => {
    const validation = configurationForm.validate();
    if (validation.hasErrors) return;

    const scheduleMeetingConfig: CaldotcomScheduleMeetingToolConfig = {
      _id: maybeExistingScheduleMeetingToolConfig?._id,
      kind: ToolKind.CALDOTCOM_SCHEDULE_MEETING,
      toolName: ToolKind.CALDOTCOM_SCHEDULE_MEETING,
      trigger: ToolTrigger.INTENT,
      enabled: configurationForm.values.enabled,
      accountId: configurationForm.values.caldotcomAccountId,
      eventTypeId: configurationForm.values.eventTypeId
    };

    const checkAvailabilityConfig: CaldotcomCheckAvailabilityToolConfig = {
      _id: maybeExistingCheckAvailabilityToolConfig?._id,
      kind: ToolKind.CALDOTCOM_CHECK_AVAILABILITY,
      toolName: ToolKind.CALDOTCOM_CHECK_AVAILABILITY,
      trigger: ToolTrigger.INTENT,
      enabled: configurationForm.values.enabled,
      accountId: configurationForm.values.caldotcomAccountId,
      eventTypeId: configurationForm.values.eventTypeId
    };

    onUpdate([scheduleMeetingConfig, checkAvailabilityConfig]);
  };

  const caldotcomAccounts = caldotcomAccountsQuery.data ?? [];
  const eventTypes = caldotcomEventTypesQuery.data ?? [];
  const selectedAccount = caldotcomAccounts.find(
    (account) => account._id === configurationForm.values.caldotcomAccountId
  );

  return (
    <ToolConfigurationPanelWrapper
      onSave={handleUpdate}
      onBack={onBack}
      onCancel={isNewToolConfig ? onBack : () => configurationForm.reset()}
      hasChanges={configurationForm.isDirty() || isNewToolConfig}
      px={0}>
      <Box>
        <ToolStatusSection
          enabled={configurationForm.values.enabled}
          onToggle={(enabled) => configurationForm.setFieldValue('enabled', enabled)}
        />
      </Box>
      <Box>
        <DroxyAccordion defaultOpenSections={[CaldotcomAppointmentPanelSections.GENERAL_SETTINGS]}>
          <DroxyAccordionSection
            title={t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.GENERAL_SETTINGS')}
            section={CaldotcomAppointmentPanelSections.GENERAL_SETTINGS}
            icon={<SectionNumber number={1} />}
            hasError={formHasFieldErrors(configurationForm, 'caldotcomAccountId', 'eventTypeId')}>
            <Stack spacing="md">
              <Alert color="blue" icon={<IconInfoCircle />}>
                <Text size="sm" weight={500}>
                  {t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.GUIDE.TITLE')}
                </Text>
                <Text size="xs">
                  {t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.GUIDE.TEXT')}{' '}
                  <Anchor
                    href={APPOINTMENT_SCHEDULING_TOOL_GUIDE_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    inherit>
                    {t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.GUIDE.LINK_TEXT')}
                  </Anchor>
                </Text>
              </Alert>
              <Select
                label={t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.ACCOUNT_SELECTION.LABEL')}
                placeholder={t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.ACCOUNT_SELECTION.PLACEHOLDER')}
                data={caldotcomAccounts.map((account) => ({
                  value: account._id,
                  label: account.accountInfo.email
                }))}
                rightSection={caldotcomAccountsQuery.isLoading ? <Loader size="xs" /> : null}
                {...configurationForm.getInputProps('caldotcomAccountId')}
              />
              <TextInput
                label={
                  <InputLabelWithInfo
                    label={t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.TIME_ZONE_SELECTION.LABEL')}
                    infoContentTranslationKey="SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.TIME_ZONE_SELECTION.INFO"
                    tooltipWidth={300}
                  />
                }
                placeholder={t(
                  'SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.TIME_ZONE_SELECTION.PLACEHOLDER'
                )}
                value={selectedAccount?.accountInfo.timeZone}
                readOnly
              />
              <Select
                label={t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.EVENT_SELECTION.LABEL')}
                placeholder={t('SMART_ASSISTANT.TOOLS.CALDOTCOM_APPOINTMENT_SCHEDULING.EVENT_SELECTION.PLACEHOLDER')}
                data={eventTypes.map((eventType) => ({
                  value: eventType.id.toString(),
                  label: eventType.title
                }))}
                rightSection={caldotcomEventTypesQuery.isLoading ? <Loader size="xs" /> : null}
                disabled={!configurationForm.values.caldotcomAccountId}
                {...configurationForm.getInputProps('eventTypeId')}
              />
            </Stack>
          </DroxyAccordionSection>
        </DroxyAccordion>
      </Box>
    </ToolConfigurationPanelWrapper>
  );
}
